import React, {createContext, useContext, useMemo} from 'react';

import {Ref as PanelBankSectionConfigRef} from '../../state/views/panelBankSectionConfig/types';
import {useRunSelectorResizingContext} from '../MultiRunWorkspace/RunSelectorResizingContext';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {usePanelBankConfigWithRefs} from './hooks/usePanelBankConfigWithRefs';

type WorkspaceSectionContextValue = {
  defaultMoveToSectionRef: PanelBankSectionConfigRef | undefined;
  shouldRenderContent: boolean;
  sectionName: string;
};

type WorkspaceSectionContextProviderProps = {
  children: React.ReactNode;
  sectionName: string;
};

const WorkspaceSectionContext = createContext<
  WorkspaceSectionContextValue | undefined
>(undefined);

export const WorkspaceSectionContextProvider = ({
  children,
  sectionName,
}: WorkspaceSectionContextProviderProps) => {
  const panelBankConfigWithRefs = usePanelBankConfigWithRefs();

  const defaultMoveToSectionRef = useMemo(() => {
    const {defaultMoveToSectionName} = panelBankConfigWithRefs.settings;
    const defaultMoveToSection = panelBankConfigWithRefs.sections.find(s => {
      return s.name === defaultMoveToSectionName;
    });
    return defaultMoveToSection?.ref;
  }, [panelBankConfigWithRefs.sections, panelBankConfigWithRefs.settings]);

  const {shouldHidePanelContents} = useWorkspaceDrawerContext();
  const {isResizing} = useRunSelectorResizingContext();
  const shouldRenderContent = !shouldHidePanelContents && !isResizing;

  const value = useMemo(
    () => ({
      shouldRenderContent,
      defaultMoveToSectionRef,
      sectionName,
    }),
    [shouldRenderContent, defaultMoveToSectionRef, sectionName]
  );

  return (
    <WorkspaceSectionContext.Provider value={value}>
      {children}
    </WorkspaceSectionContext.Provider>
  );
};

export const useWorkspaceSectionContext = () => {
  const value = useContext(WorkspaceSectionContext);

  if (value == null) {
    throw new Error(
      'useWorkspaceSectionContext must be used within a WorkspaceSectionContextProvider'
    );
  }

  return value;
};
